/**
 * Message templates — the operator-authored texts the platform sends to its
 * users, and the grammar of the `{{key}}` placeholders inside them.
 *
 * Shared, not admin-only: the admin surface *authors* templates, but they are
 * *rendered* wherever a message is composed (a backend sender, a worker job,
 * a preview in the admin form). Every one of those depends on this single
 * declaration of the channel set, the variable set and the scanner, so the
 * validator that admits a template and the renderer that fills it cannot
 * disagree about what a placeholder is.
 *
 * The scanner is a **single-pass linear** walk by char code: it never builds a
 * `RegExp` from user-supplied text and never backtracks, so it is ReDoS-safe
 * by construction over the length-bounded bodies the API admits.
 */
/**
 * The channels a template can be written for. Mirrors the backend's
 * `MessageTemplateChannel` Postgres enum. The channel decides the subject
 * rule: `EMAIL` requires a subject, `WHATSAPP` forbids one (a chat message has
 * no subject line).
 */
export const MESSAGE_TEMPLATE_CHANNELS = ["EMAIL", "WHATSAPP"];
/**
 * The languages a template can be written in — the organization language set
 * (`SupportedLanguage`), verbatim: a sender picks the template whose language
 * is the recipient organization's `defaultLanguage`, so the two sets must be
 * one set. Stored as `text` on the row (like `organizations.default_language`),
 * validated here.
 */
export const MESSAGE_TEMPLATE_LANGUAGES = [
    "en_US",
    "pt_BR",
];
/**
 * Operator-facing label per language (the admin SPA is English-only). Keyed
 * on the enum, not on the tuple, so a language added to `SupportedLanguage`
 * is a compile error here — the reminder to extend the tuple above, whose
 * `satisfies` only proves the converse (nothing outside the enum).
 */
export const MESSAGE_TEMPLATE_LANGUAGE_LABELS = {
    en_US: "English",
    pt_BR: "Portuguese (Brazil)",
};
export function isMessageTemplateLanguage(value) {
    return (typeof value === "string" &&
        MESSAGE_TEMPLATE_LANGUAGES.includes(value));
}
/**
 * The closed set of variable keys a template's subject or body may reference,
 * resolved from the recipient (a tenant user) and their organization at send
 * time. A `{{key}}` outside this set is rejected when the template is saved,
 * so a sender never meets a key it cannot resolve.
 *
 * Extending it is one entry here; the admin form's "insert variable" chips and
 * the backend allow-list both read this array.
 */
export const MESSAGE_TEMPLATE_VARIABLE_KEYS = [
    "user_first_name",
    "user_last_name",
    "user_email",
    "organization_name",
    "app_url",
    "trial_end_date",
];
/** Operator-facing label per variable (the admin SPA is English-only). */
export const MESSAGE_TEMPLATE_VARIABLE_LABELS = {
    user_first_name: "Recipient's first name",
    user_last_name: "Recipient's last name",
    user_email: "Recipient's email",
    organization_name: "Recipient's organization",
    app_url: "Link to the app",
    trial_end_date: "Trial end date (empty when the organization has no trial)",
};
/** Bounds the API admits; the form and the route schema both read them. */
export const MESSAGE_TEMPLATE_NAME_MAX_LENGTH = 120;
export const MESSAGE_TEMPLATE_SUBJECT_MAX_LENGTH = 200;
export const MESSAGE_TEMPLATE_BODY_MAX_LENGTH = 20_000;
export const MESSAGE_TEMPLATE_DESCRIPTION_MAX_LENGTH = 500;
/** Max key length; also the scanner's per-token bound (keeps the scan linear). */
const MAX_KEY_LENGTH = 64;
const OPEN_BRACE = 0x7b; // "{"
const CLOSE_BRACE = 0x7d; // "}"
/** `[A-Za-z0-9_]` by char code — no allocation, no regex, per character. */
function isKeyChar(code) {
    return ((code >= 0x30 && code <= 0x39) || // 0-9
        (code >= 0x41 && code <= 0x5a) || // A-Z
        (code >= 0x61 && code <= 0x7a) || // a-z
        code === 0x5f // _
    );
}
/**
 * Try to read a `{{key}}` token whose opening `{{` sits at `start`. Returns the
 * token, or `null` when the run is not a valid key (empty, over-long, or a
 * non-key char such as whitespace) or the closing `}}` is missing — in which
 * case the caller treats the braces as literal text.
 */
function readToken(text, start) {
    const keyStart = start + 2;
    let cursor = keyStart;
    const maxKeyEnd = Math.min(text.length, keyStart + MAX_KEY_LENGTH);
    while (cursor < maxKeyEnd && isKeyChar(text.charCodeAt(cursor))) {
        cursor++;
    }
    if (cursor - keyStart < 1) {
        return null;
    }
    // Require the run to be closed *immediately* by "}}" — `{{ key }}` (a space
    // breaks the run before the braces) and `{{keythatistoolong…` both fail here.
    if (text.charCodeAt(cursor) !== CLOSE_BRACE ||
        text.charCodeAt(cursor + 1) !== CLOSE_BRACE) {
        return null;
    }
    return { key: text.slice(keyStart, cursor), start, end: cursor + 2 };
}
/**
 * Every well-formed `{{key}}` token in `text`, in source order. Stray or
 * unbalanced braces that do not form a token (`{{ key }}`, `{{}}`, a lone
 * `{{`) are literal text and do not appear here.
 */
export function findMessageTemplatePlaceholders(text) {
    const found = [];
    const length = text.length;
    let index = 0;
    while (index < length) {
        if (text.charCodeAt(index) === OPEN_BRACE &&
            text.charCodeAt(index + 1) === OPEN_BRACE) {
            const token = readToken(text, index);
            if (token) {
                found.push(token);
                index = token.end;
                continue;
            }
        }
        index++;
    }
    return found;
}
const ALLOWED_KEYS = new Set(MESSAGE_TEMPLATE_VARIABLE_KEYS);
/**
 * The keys of every placeholder in `text` that is NOT one of the fixed
 * variables, deduplicated, in source order. Empty means the text is valid.
 */
export function findDisallowedMessageTemplateKeys(text) {
    const seen = new Set();
    for (const { key } of findMessageTemplatePlaceholders(text)) {
        if (!ALLOWED_KEYS.has(key))
            seen.add(key);
    }
    return [...seen];
}
/**
 * Substitute every well-formed `{{key}}` placeholder in `text` with its value
 * from `values`. A key present in `values` is replaced by its (possibly empty)
 * value; a key absent from `values` is left as the literal `{{key}}` token so a
 * caller that only passes the values it knows never silently drops a variable.
 * Malformed brace runs are left untouched.
 */
export function renderMessageTemplate(text, values) {
    let out = "";
    let literalStart = 0;
    for (const token of findMessageTemplatePlaceholders(text)) {
        const value = Object.hasOwn(values, token.key)
            ? values[token.key]
            : undefined;
        if (value === undefined)
            continue;
        out += text.slice(literalStart, token.start) + value;
        literalStart = token.end;
    }
    return out + text.slice(literalStart);
}
//# sourceMappingURL=MessageTemplate.js.map